/* @flow */
// $FlowFixMe
import template from 'underscore/modules/template.js';
import adconfig from '../adconfig';
import adloader from '../adloader';
import containerHelper from '../container-helper';
import _adserverbase from './adserverbase';
import { BrowserUtil } from '../utils/browserutil';
import { logDebug, logError } from '../utils/logger';
import { extend, isObject } from '../utils/coreutils';
import { isOptedOut } from '../utils/privacyutils';
import getCVars from '../utils/gputils';
import isdebugEnabled from '../utils/debug';
import { setCookie, deleteCookie } from '../utils/cookie';

// We are going to use GAM style templating for interpolation. Let's use %%VAR%% here
// Reference: Similar to staticas.js template settings
const templateSettings = {
  interpolate: /%%(.*?)%%/g
};

// Video script tag - included in template when loading in iframe
// Reference: https://open.video/ - Ezoic video player script
const EZOIC_VIDEO_SCRIPT_TAG =
  '<script async data-ezscrex="false" data-cfasync="false" src="https://open.video/video.js"></script>';

// Base style for video ad wrapper div
const EZOIC_VIDEO_BASE_STYLE = 'width: 100%; margin: 0 auto; display: block;';

// Single video ad creative template - conditionally includes max-width and script
// Script is included only when loading in iframe (iframe !== false)
// Reference: https://docs.ezoic.com/docs/ezoicads/implementation/
const EZOIC_VIDEO_AD_CREATIVE_TEMPLATE_STRING =
  '<div style="%%STYLE%%"><script data-ezscrex="false" data-cfasync="false">(window.openVideoPlayers = window.openVideoPlayers || []).push({target: document.currentScript});</script>%%VIDEO_SCRIPT%%</div>';

// Pre-compile template function for performance
// $FlowFixMe - template function type
const FN_EZOIC_VIDEO_AD_CREATIVE_TEMPLATE = template(
  EZOIC_VIDEO_AD_CREATIVE_TEMPLATE_STRING,
  templateSettings
);

// Placeholder div template for banner ads with optional inline CSS
// Reference: Similar to video template, allows custom styling per position
const EZOIC_PLACEHOLDER_ID_PREFIX = 'ezoic-pub-ad-placeholder-';
const EZOIC_PLACEHOLDER_TEMPLATE_STRING =
  '<div id="%%PLACEHOLDER_ID%%" style="%%PLACEHOLDER_STYLE%%"></div>';

// Pre-compile template function for performance
// $FlowFixMe - template function type
const FN_EZOIC_PLACEHOLDER_TEMPLATE = template(
  EZOIC_PLACEHOLDER_TEMPLATE_STRING,
  templateSettings
);

// Ezoic ad type constants - extensible for future ad types
// Reference: https://docs.ezoic.com/docs/ezoicads/implementation/
const EZOIC_AD_TYPES: { [string]: string } = {
  BANNER: 'banner',
  VIDEO: 'video'
};

// Pre-computed valid ad types array for efficient validation
const VALID_EZOIC_AD_TYPES: Array<string> = [
  EZOIC_AD_TYPES.BANNER,
  EZOIC_AD_TYPES.VIDEO
];

interface ezoicPlacement {
  id: number;
  sizes?: Array<string>;
  required?: boolean;
}

interface ezstandalone {
  cmd: Array<cbFunc>;
  showAds(Array<ezoicPlacement>): void;
  refreshAds(...Array<number>): void;
  destroyPlaceholders(...Array<number>): void;
  config({ limitCookies?: boolean }): void;
}

interface ezoicIdentity {
  queue: Array<cbFunc>;
  setIdentity({ sha1?: string, md5?: string, sha256?: string }): void;
}

/**
 * Implements Ezoic ad server functionality
 *
 * @class
 * @exports adserver/ezoicAS
 * @requires  module:coreutils
 * @requires  module:adconfig
 * @requires  module:adloader
 * @requires  module:adserverbase
 * @requires module:containerHelper
 * @requires  module:browserutil
 * @requires module:logger
 * @requires module:privacyutils
 */
class _ezoic extends _adserverbase {
  _libAlreadyLoaded: boolean;

  _oldLib: string;

  _defConfig: ezoicASConfig;

  _config: ezoicASConfig;

  _adTypeHandlers: { [string]: (Array<string>) => void };

  _shownPositions: Set<string>;

  constructor(name: string) {
    super();
    this._name = name;
    this._defConfig = extend(true, {}, this._defConfig, {
      lib: '',
      timeout: 10000
    });
    this._config = extend(true, this._defConfig);
    this._libAlreadyLoaded = false;
    this._oldLib = ''; // Store older lib url to prevent re-loading same lib again after updating config
    this._shownPositions = new Set(); // Track positions that have called showAds

    // Initialize ad type handlers map once - strategy pattern for extensible ad type handling
    // Use arrow functions to call methods dynamically so spies work correctly in tests
    this._adTypeHandlers = {
      [EZOIC_AD_TYPES.BANNER]: (positions: Array<string>) => {
        this._loadBannerAds(positions);
      },
      [EZOIC_AD_TYPES.VIDEO]: (positions: Array<string>) => {
        this._loadVideoAds(positions);
      }
    };
  }

  /**
   * Initialize the object - called from adserver initialize
   *
   * @param {window} w - window object
   * @param {Document} d - document object
   * @param {module:globalParams.globalParams} g - global param object
   * @returns {undefined}
   */
  init(w: WindowProxy, d: Document, g: globalParam) {
    super.init(w, d, g);
    // Initialize Ezoic standalone object and command queue
    // Reference: https://docs.ezoic.com/docs/ezoicads/integration/#header-script
    // $FlowFixMe - window properties for Ezoic
    const win = (this._win: any);
    win.ezstandalone = win.ezstandalone || {
      cmd: []
    };
    // Initialize Ezoic Identity object and queue
    // Reference: https://docs.ezoic.com/docs/identity/getting-started/
    win.ezoicIdentity = win.ezoicIdentity || {
      queue: []
    };
  }

  /**
   * Check if any position uses ezoicAS ad server
   *
   * @returns {boolean} - true if at least one position uses ezoicAS
   * @private
   */
  _hasEzoicPositions(): boolean {
    const { settings: { posMap = {} } = {} } = adconfig.get() || {};
    return Object.values(posMap).some((posSettings: mixed) => {
      if (!isObject(posSettings)) {
        return false;
      }
      // $FlowFixMe - posConfigInfo has optional 'as' property
      const posConfig: posConfigInfo = (posSettings: posConfigInfo);
      return posConfig && posConfig.as === this._name;
    });
  }

  /**
   * Setup Ezoic Identity - handle opt-out and email hash passing
   * Reference: https://docs.ezoic.com/docs/identity/getting-started/
   *
   * @returns {undefined}
   * @private
   */
  _setupEzoicIdentity() {
    const optedOut = isOptedOut();

    if (optedOut) {
      // For opted-out users: limit cookie usage
      // Reference: https://docs.ezoic.com/docs/ezoicadsadvanced/advanced/#limit-cookies
      // $FlowFixMe - window properties for Ezoic
      const win = (this._win: any);
      const ezstandaloneObj: ?ezstandalone = win.ezstandalone;
      if (ezstandaloneObj && ezstandaloneObj.cmd) {
        ezstandaloneObj.cmd.push(() => {
          // Re-fetch object from window when command executes to avoid stale reference
          const currentEzstandalone: ?ezstandalone = win.ezstandalone;
          if (currentEzstandalone && currentEzstandalone.config) {
            currentEzstandalone.config({ limitCookies: true });
            logDebug(`${this._name} limitCookies set for opted-out user`);
          }
        });
      }
      // Do NOT pass email hashes for opted-out users
      return;
    }

    // For non-opted-out users: pass email hashes
    const cvars = getCVars();
    const {
      _uh: sha1Email = '',
      _uh4: md5Email = '',
      _uh6: sha256Email = ''
    } = cvars;

    // Only proceed if we have at least one email hash
    if (!sha1Email.length && !md5Email.length && !sha256Email.length) {
      logDebug(`${this._name} no email hashes available for Identity`);
      return;
    }

    // Build identity object with available hashes
    const identityData = {};
    if (sha1Email.length) {
      identityData.sha1 = sha1Email;
    }
    if (md5Email.length) {
      identityData.md5 = md5Email;
    }
    if (sha256Email.length) {
      identityData.sha256 = sha256Email;
    }

    // Use queue pattern to ensure it runs after library loads
    // Reference: https://docs.ezoic.com/docs/identity/getting-started/
    // $FlowFixMe - window properties for Ezoic
    const win = (this._win: any);
    const ezoicIdentityObj: ?ezoicIdentity = win.ezoicIdentity;
    if (ezoicIdentityObj && ezoicIdentityObj.queue) {
      ezoicIdentityObj.queue.push(() => {
        // Re-fetch object from window when callback executes to avoid stale reference
        const currentEzoicIdentity: ?ezoicIdentity = win.ezoicIdentity;
        if (currentEzoicIdentity && currentEzoicIdentity.setIdentity) {
          currentEzoicIdentity.setIdentity(identityData);
          logDebug(
            `${this._name} setIdentity called with ${Object.keys(identityData).join(', ')}`
          );
        }
      });
    }
  }

  /**
   * Setup Ezoic debug cookie - set ez_js_debugger cookie when debugging is enabled,
   * delete it when debugging is disabled
   * Reference: Ezoic library supports debugging when ez_js_debugger cookie is set to '1'
   *
   * @returns {undefined}
   * @private
   */
  _setupDebugCookie() {
    if (isdebugEnabled()) {
      // Set ez_js_debugger cookie to enable Ezoic's built-in debugging
      setCookie('ez_js_debugger', '1', this._doc);
      logDebug(`${this._name} ez_js_debugger cookie set for debugging`);
    } else {
      // Delete cookie when debug is disabled
      deleteCookie('ez_js_debugger', this._doc);
      logDebug(`${this._name} ez_js_debugger cookie deleted (debug disabled)`);
    }
  }

  _preUpdateConfig() {
    // Convert array to string for comparison if needed
    const libValue = this._config.lib || '';
    this._oldLib = Array.isArray(libValue) ? libValue.join(',') : libValue;
  }

  _postUpdateConfig() {
    // Handle both string and array lib values
    const libValue = this._config.lib;
    const isEmpty =
      !libValue ||
      (Array.isArray(libValue) && libValue.length === 0) ||
      (typeof libValue === 'string' && !libValue.length);

    if (isEmpty) {
      return;
    }

    // Only load library if at least one position uses this ad server
    if (!this._hasEzoicPositions()) {
      logDebug(
        `${this._name} no positions use ${this._name}, skipping library load`
      );
      return;
    }

    // Check if lib is array or string for comparison
    const libValueForComparison = Array.isArray(libValue)
      ? libValue.join(',')
      : libValue;

    // Do not load already loaded library
    if (this._libAlreadyLoaded && this._oldLib === libValueForComparison) {
      // Setup identity even if library already loaded (in case config changed)
      this._setupEzoicIdentity();
      return;
    }

    // load the library only for modern browsers
    if (BrowserUtil.isOldIE()) {
      return;
    }

    // Setup debug cookie before loading library (if debugging is enabled)
    this._setupDebugCookie();

    // Load library(ies) asynchronously - supports both string and array
    this._loadLibrary(
      libValue,
      true, // async
      this._config.libLoadMode || 'parallel'
    );
    this._libAlreadyLoaded = true;

    // Setup Ezoic Identity after library loads (or via queue)
    this._setupEzoicIdentity();
  }

  clearAdRequests(
    _pageName: string,
    posList: Array<string> = [] /*,
    bRotation: boolean = false*/
  ) {
    // $FlowFixMe - window properties for Ezoic
    const win = (this._win: any);
    const ezstandaloneObj: ?ezstandalone = win.ezstandalone;
    if (!ezstandaloneObj) {
      logError(`${this._name} ezstandalone not available`);
      this._shownPositions.clear();
      return;
    }

    const oldPositions = new Set(this._shownPositions);
    const newPositions = new Set(posList);
    const destroyPlacementIds = new Set();
    const positionsToRemove = new Set();

    oldPositions.forEach(pos => {
      const placementId = this._getPlacementId(pos);
      if (!placementId) {
        return;
      }

      if (newPositions.has(pos)) {
        destroyPlacementIds.add(placementId);
        positionsToRemove.add(pos);
        return;
      }

      const placeholderId = `${EZOIC_PLACEHOLDER_ID_PREFIX}${placementId}`;
      if (!this._doc.getElementById(placeholderId)) {
        destroyPlacementIds.add(placementId);
        positionsToRemove.add(pos);
      }
    });

    if (destroyPlacementIds.size > 0 && ezstandaloneObj.cmd) {
      const placementIds = [...destroyPlacementIds];
      ezstandaloneObj.cmd.push(() => {
        const currentEzstandalone: ?ezstandalone = win.ezstandalone;
        if (currentEzstandalone && currentEzstandalone.destroyPlaceholders) {
          currentEzstandalone.destroyPlaceholders(...placementIds);
          logDebug(
            `${this._name} destroyPlaceholders called for ${placementIds.length} placement(s)`
          );
        }
      });
    }

    positionsToRemove.forEach(pos => {
      this._shownPositions.delete(pos);
    });
  }

  /**
   * Get Ezoic placement ID from position config
   *
   * @param {string} pos - position
   * @returns {number} - Placement ID or 0 if not found
   * @private
   */
  _getPlacementId(pos: string = ''): number {
    const posSettings: posConfigInfo = adconfig.getPosSettings(pos);
    // $FlowFixMe - ezoicPlacementId is optional property in posConfigInfo
    const { ezoicPlacementId = 0 } = (posSettings: any);
    return typeof ezoicPlacementId === 'number' ? ezoicPlacementId : 0;
  }

  /**
   * Get Ezoic ad type from position config
   *
   * @param {string} pos - position
   * @returns {string} - Ad type (EZOIC_AD_TYPES.BANNER or EZOIC_AD_TYPES.VIDEO)
   * @private
   */
  _getAdType(pos: string = ''): string {
    const posSettings: posConfigInfo = adconfig.getPosSettings(pos);
    // $FlowFixMe - ezoicAdType is optional property in posConfigInfo
    const { ezoicAdType = EZOIC_AD_TYPES.BANNER } = (posSettings: any);
    if (typeof ezoicAdType !== 'string' || ezoicAdType.length === 0) {
      return EZOIC_AD_TYPES.BANNER;
    }
    // Validate that ad type is a valid enum value, default to BANNER if invalid
    return VALID_EZOIC_AD_TYPES.includes(ezoicAdType)
      ? ezoicAdType
      : EZOIC_AD_TYPES.BANNER;
  }

  /**
   * Convert size array to Ezoic format string
   *
   * @param {posSizes} sizes - Array of [width, height] arrays
   * @returns {Array<string>} - Array of size strings like ['300x250', '728x90']
   * @private
   */
  _convertSizes(sizes: posSizes): Array<string> {
    if (!Array.isArray(sizes) || sizes.length === 0) {
      return [];
    }
    return sizes
      .filter(size => Array.isArray(size) && size.length === 2)
      .map(([w, h]) => `${w}x${h}`);
  }

  /**
   * Get video ad template for position
   * Calculates max width from sizes and conditionally includes video script based on iframe loading
   * If maxWidth is 0, omits max-width style property (relies on width: 100%)
   * Script is included only when loading in iframe (iframe !== false)
   *
   * @param {string} pos - Position name
   * @returns {string} - Processed video ad template
   * @private
   */
  _getVideoAdTemplate(pos: string): string {
    // Get position config to access sizes and iframe setting
    const posSettings: posConfigInfo = adconfig.getPosSettings(pos);
    const sizes = posSettings.sizes || [];

    // Check if this position will be loaded in iframe
    // If iframe === false, script is in <head> (don't include in template)
    // If iframe === undefined or true, script needs to be in template (iframe context)
    const useIframe = posSettings.iframe !== false;
    const videoScript = useIframe ? EZOIC_VIDEO_SCRIPT_TAG : '';

    // Calculate max width from sizes array
    let maxWidth = 0;
    if (Array.isArray(sizes) && sizes.length > 0) {
      try {
        // Extract widths and find max, with fallback to 0
        maxWidth = Math.max(0, ...sizes.map(([w]) => w));
      } catch {
        // Defensive: handle runtime errors from invalid destructuring
        maxWidth = 0;
      }
    }

    // Build style string - conditionally include max-width
    let style = EZOIC_VIDEO_BASE_STYLE;
    if (maxWidth > 0) {
      style = `max-width: ${maxWidth}px; ${style}`;
    }

    // Process template with style and conditional script
    try {
      const macros = {
        STYLE: style,
        VIDEO_SCRIPT: videoScript
      };
      // $FlowFixMe - template function type
      return FN_EZOIC_VIDEO_AD_CREATIVE_TEMPLATE(macros);
    } catch (error) {
      logError(
        `${this._name} Exception in processing video ad template: ${error}`
      );
      // Fallback to template without max-width
      return EZOIC_VIDEO_AD_CREATIVE_TEMPLATE_STRING.replace(
        '%%STYLE%%',
        style
      ).replace('%%VIDEO_SCRIPT%%', videoScript);
    }
  }

  /**
   * Ensure placeholder exists for banner ad (check and create if missing)
   *
   * @param {string} pos - Position name
   * @param {number} placementId - Ezoic placement ID
   * @returns {boolean} - true if placeholder exists or was created successfully, false otherwise
   * @private
   */
  _ensurePlaceholderForBanner(pos: string, placementId: number): boolean {
    // Get position div
    const adContainerInfo: adInfo = containerHelper.getAdContainerInfo(
      pos,
      false
    );
    const positionDiv = this._doc.getElementById(adContainerInfo.cID);
    if (!positionDiv) {
      logError(`${this._name} position div not found: ${adContainerInfo.cID}`);
      return false;
    }

    const placeholderId = `${EZOIC_PLACEHOLDER_ID_PREFIX}${placementId}`;
    let placeholderDiv = this._doc.getElementById(placeholderId);

    // If placeholder already exists, return true
    if (placeholderDiv) {
      return true;
    }

    // Get optional CSS from position config
    const posSettings: posConfigInfo = adconfig.getPosSettings(pos);
    // $FlowFixMe - ezoicBannerXtraCSS is optional property in posConfigInfo
    const ezoicBannerXtraCSS: string =
      (posSettings: posConfigInfo).ezoicBannerXtraCSS || '';
    const placeholderStyle = ezoicBannerXtraCSS || '';

    // Generate placeholder div HTML using template
    let placeholderHtml = '';
    try {
      const macros = {
        PLACEHOLDER_ID: placeholderId,
        PLACEHOLDER_STYLE: placeholderStyle
      };
      // $FlowFixMe - template function type
      placeholderHtml = FN_EZOIC_PLACEHOLDER_TEMPLATE(macros);
    } catch (error) {
      logError(
        `${this._name} Exception in processing placeholder template: ${error}`
      );
      return false;
    }

    // Insert placeholder div using innerHTML (consistent with loadNonIframeAd pattern)
    // Reference: Similar to video ad insertion via loadNonIframeAd which uses innerHTML
    // Browser support: innerHTML is supported in IE4+ (compatible with IE6+ requirement)
    positionDiv.innerHTML = placeholderHtml;
    placeholderDiv = this._doc.getElementById(placeholderId);
    if (!placeholderDiv) {
      logError(
        `${this._name} failed to create placeholder div: ${placeholderId}`
      );
      return false;
    }

    logDebug(`${this._name} created placeholder div: ${placeholderId}`);
    return true;
  }

  /**
   * Loads Ezoic ads
   *
   * @param {Array<string>} posList - position list array
   * @param {object.<string, module:_bidbase.hbPosData>} o - header bidding return object
   * @param {object} customVars - custom variable to be populated inside iframe
   * @param {boolean} bRotation - For ad rotation?
   * @returns {undefined}
   */
  loadAds(
    posList: Array<string> = [],
    o: hbPartnerData = {},
    customVars: CustomVars = {},
    bRotation: boolean = false
  ) {
    // eslint-disable-next-line no-unused-vars
    const _ = { o, customVars, bRotation }; // Parameters reserved for future use
    if (!this._libAlreadyLoaded) {
      logDebug(`${this._name} library not loaded, skipping ad load`);
      return;
    }

    // Group positions by ad type using strategy pattern for extensibility
    const positionsByType: { [string]: Array<string> } = Object.fromEntries(
      VALID_EZOIC_AD_TYPES.map(adType => [adType, []])
    );

    posList.forEach(pos => {
      const adType = this._getAdType(pos);
      positionsByType[adType].push(pos);
    });

    // Execute handlers for each ad type that has positions
    Object.keys(positionsByType).forEach(adType => {
      const positions = positionsByType[adType];
      const handler = this._adTypeHandlers[adType];
      if (handler) {
        handler(positions);
      } else if (positions.length > 0) {
        // Only log error if there are actually positions to process
        logError(
          `${this._name} unknown ad type: ${adType}. Supported types: ${Object.values(EZOIC_AD_TYPES).join(', ')}`
        );
      }
    });

    adloader.checkQueue();
  }

  /**
   * Load banner ads
   *
   * @param {Array<string>} posList - Banner ad positions
   * @returns {undefined}
   * @private
   */
  _loadBannerAds(posList: Array<string>) {
    if (posList.length === 0) {
      return;
    }

    // Get ezstandalone object from window - initialized in init()
    // $FlowFixMe - window properties for Ezoic
    const win = (this._win: any);
    const ezstandaloneObj: ?ezstandalone = win.ezstandalone;
    if (!ezstandaloneObj) {
      logError(`${this._name} ezstandalone not available`);
      return;
    }

    const placements: Array<ezoicPlacement> = [];
    const positionsToTrack: Array<string> = [];

    posList.forEach(pos => {
      const placementId = this._getPlacementId(pos);
      if (!placementId) {
        logError(`${this._name} missing ezoicPlacementId for ${pos}`);
        return;
      }

      // Ensure placeholder exists (create if it doesn't)
      if (!this._ensurePlaceholderForBanner(pos, placementId)) {
        logError(
          `${this._name} failed to create placeholder for ${placementId}`
        );
        return;
      }

      const posSettings = adconfig.getPosSettings(pos);
      const sizes = this._convertSizes(posSettings.sizes || []);
      const placementObj: ezoicPlacement = {
        id: placementId,
        required: true
      };
      if (sizes.length > 0) {
        placementObj.sizes = sizes;
      }
      placements.push(placementObj);
      positionsToTrack.push(pos);
      logDebug(`${this._name} placement ${placementId} will show ad`);
    });

    // Batch process new placements
    // Always use array format with sizes for consistency
    if (placements.length > 0) {
      ezstandaloneObj.cmd.push(() => {
        // Re-fetch object from window when command executes to avoid stale reference
        const currentEzstandalone: ?ezstandalone = win.ezstandalone;
        if (currentEzstandalone && currentEzstandalone.showAds) {
          currentEzstandalone.showAds(placements);
          logDebug(
            `${this._name} showAds called for ${placements.length} placement(s)`
          );
        }
      });
      positionsToTrack.forEach(pos => {
        this._shownPositions.add(pos);
      });
    }
  }

  /**
   * Load video ads
   * Note: Ezoic allows only one video ad per page
   *
   * @param {Array<string>} posList - Video ad positions
   * @returns {undefined}
   * @private
   */
  _loadVideoAds(posList: Array<string>) {
    if (posList.length === 0) {
      return;
    }

    // Ezoic allows only one video ad per page
    if (posList.length > 1) {
      logError(
        `${this._name} multiple video ads detected (${posList.length}). Ezoic allows only one video ad per page. Only the first one will be loaded.`
      );
    }

    // Only process the first video ad position
    const [videoPos] = posList;
    if (!videoPos) {
      return;
    }

    // Get position div
    const adContainerInfo: adInfo = containerHelper.getAdContainerInfo(
      videoPos,
      false
    );
    const positionDiv = this._doc.getElementById(adContainerInfo.cID);
    if (!positionDiv) {
      logError(`${this._name} position div not found: ${adContainerInfo.cID}`);
      return;
    }

    // Get video ad template (calculates max width from sizes and processes template)
    const videoAdCreative = this._getVideoAdTemplate(videoPos);

    // Inject video ad creative script
    logDebug(`${this._name} injecting video ad script for ${videoPos}`);
    adloader.load(
      videoPos,
      videoAdCreative,
      {},
      this._name,
      false,
      adContainerInfo.cID
    );
  }
}

const ezoic: _ezoic = new _ezoic('ezoicAS');
export default ezoic;
export { EZOIC_AD_TYPES };
